// src/pages/Territories.tsx — where Green Maestro grows next, and whether each ZIP is covered (OPS-FINAL D4).
// Launched: coverage vs demand. Prospects: the scouting view the old Territory Command Center had.
// Every census figure stays in the ZIP panel (owner decision 7). Race and language figures are census
// context for how to talk to a ZIP; they are never used for scoring, sorting, pricing or dispatch.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { canApply, LIFECYCLE_LABEL, ops, type FlywheelSettings, type LifecycleStatus, type LitTest, type OnboardingStepKey, type RouteBoard, type RouteBoardRow, type RouteSizeMix, type RouteState, type TerritoryRow, type ZipDetail, type ZipOnboarding } from "../lib/ops";
import { count, dateTime, dollars, money, parseDollars, pct1, toDollarText } from "../lib/format";
import { useOperator, useToast } from "../lib/context";
import { Chip, Field, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";
import { Panel } from "../components/Panel";

type View = "launched" | "prospect";
type Side = { kind: "zip"; zip: string } | { kind: "add" } | { kind: "import" } | null;

const STATUSES: LifecycleStatus[] = ["prospect", "testing", "building", "launched", "ramping", "proven", "paused"];

export function LifecycleChip({ status }: { status: LifecycleStatus | null }) {
  const s = status ?? "prospect";
  const tone = s === "paused" ? "warn" : s === "launched" || s === "proven" || s === "ramping" ? "info" : s === "prospect" ? "muted" : undefined;
  return <Chip tone={tone}>{LIFECYCLE_LABEL[s]}</Chip>;
}

export default function Territories() {
  const { role } = useOperator();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<TerritoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>("launched");
  const [refreshing, setRefreshing] = useState(false);
  const zipParam = params.get("zip");
  const [side, setSide] = useState<Side>(zipParam ? { kind: "zip", zip: zipParam } : null);

  const load = useCallback(async () => {
    try {
      setRows(await ops.territories());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const openZip = (zip: string | null) => {
    setSide(zip ? { kind: "zip", zip } : null);
    const next = new URLSearchParams();
    if (zip) next.set("zip", zip);
    setParams(next, { replace: true });
  };

  async function refresh() {
    setRefreshing(true);
    try {
      const r = await ops.refreshCensus();
      toast(`✓ Census and mail routes refreshed for ${r.zips_fetched} ZIPs`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  const launched = rows?.filter((r) => r.view === "launched") ?? [];
  const prospects = rows?.filter((r) => r.view === "prospect") ?? [];
  const shown = view === "launched" ? launched : prospects;
  const allowed = canApply(role);

  const panel =
    side?.kind === "zip" ? (
      <ZipPanel key={side.zip} zip={side.zip} row={rows?.find((r) => r.zip === side.zip) ?? null} onClose={() => openZip(null)} onChanged={load} />
    ) : side?.kind === "add" ? (
      <AddZipPanel onClose={() => setSide(null)} onDone={async (zip) => { await load(); openZip(zip); }} />
    ) : side?.kind === "import" ? (
      <ImportPanel onClose={() => setSide(null)} onDone={async () => { setSide(null); await load(); }} />
    ) : undefined;

  return (
    <PageFrame title="Territories" lede="Where we grow next, and whether each ZIP is covered." panel={panel}>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Which ZIPs">
          <button type="button" className={view === "launched" ? "seg on" : "seg"} aria-pressed={view === "launched"} onClick={() => setView("launched")}>
            Launched ({launched.length})
          </button>
          <button type="button" className={view === "prospect" ? "seg on" : "seg"} aria-pressed={view === "prospect"} onClick={() => setView("prospect")}>
            Prospects ({prospects.length})
          </button>
        </div>
        {allowed && (
          <div className="toolbar-actions">
            <button type="button" className="btn" onClick={() => setSide({ kind: "add" })}>
              Add ZIP →
            </button>
            <button type="button" className="btn" onClick={() => setSide({ kind: "import" })}>
              Import ZIPs →
            </button>
            <button type="button" className="btn quiet" disabled={refreshing} onClick={refresh}>
              {refreshing ? "Refreshing…" : "Refresh census data"}
            </button>
          </div>
        )}
      </div>

      {error && <Notice kind="error">{error}</Notice>}
      {!rows && !error && <Loading what="territories" />}
      {rows && shown.length === 0 && (
        <p className="empty-state">{view === "launched" ? "No launched ZIPs yet. Move a prospect to Testing or Launched from its panel." : "No prospects. Add a ZIP to start scouting."}</p>
      )}
      {rows && shown.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              {view === "launched" ? (
                <tr>
                  <th scope="col">ZIP</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="num">Maestros</th>
                  <th scope="col" className="num">Paid jobs, 30 days</th>
                  <th scope="col" className="num">Median time to assign</th>
                  <th scope="col" className="num">Scan rate</th>
                </tr>
              ) : (
                <tr>
                  <th scope="col">ZIP</th>
                  <th scope="col">Area</th>
                  <th scope="col" className="num">Score</th>
                  <th scope="col" className="num">Owner-occupied</th>
                  <th scope="col" className="num">Median income</th>
                  <th scope="col" className="num">Mail homes</th>
                </tr>
              )}
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr
                  key={r.zip}
                  className={side?.kind === "zip" && side.zip === r.zip ? "row-link selected" : "row-link"}
                  tabIndex={0}
                  onClick={() => openZip(r.zip)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openZip(r.zip);
                    }
                  }}
                  aria-label={`Open ZIP ${r.zip}`}
                >
                  {view === "launched" ? (
                    <>
                      <td data-label="ZIP">
                        <span className="cell-main mono">{r.zip}</span>
                        <span className="cell-sub">{r.label ?? ""}</span>
                      </td>
                      <td data-label="Status"><LifecycleChip status={r.lifecycle_status} /></td>
                      <td data-label="Maestros" className="num">{r.maestros_serving}</td>
                      <td data-label="Paid jobs, 30 days" className="num">{r.paid_jobs_30d}</td>
                      <td data-label="Median time to assign" className="num">{r.median_hours_to_assign === null ? "—" : `${r.median_hours_to_assign} h`}</td>
                      <td data-label="Scan rate" className="num">{r.scan_rate_pct === null ? <span className="muted">{r.scans} scans</span> : pct1(r.scan_rate_pct)}</td>
                    </>
                  ) : (
                    <>
                      <td data-label="ZIP" className="mono">{r.zip}</td>
                      <td data-label="Area">{r.label ?? "—"}</td>
                      <td data-label="Score" className="num">{r.total_score ?? "—"}</td>
                      <td data-label="Owner-occupied" className="num">{pct1(r.owner_occupied_pct)}</td>
                      <td data-label="Median income" className="num">{dollars(r.median_hh_income)}</td>
                      <td data-label="Mail homes" className="num">{count(r.mail_homes)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {view === "launched" && rows && launched.length > 0 && (
        <p className="hint">Scan rate needs a mailer count on the ZIP's campaigns; until then the raw scan count shows.</p>
      )}
    </PageFrame>
  );
}

// ── ZIP panel ──────────────────────────────────────────────

const CENSUS_ROWS: { key: string; label: string; fmt: (v: unknown) => string }[] = [
  { key: "population", label: "Population", fmt: (v) => count(v as number) },
  { key: "median_age", label: "Median age", fmt: (v) => (v === null || v === undefined ? "—" : String(v)) },
  { key: "median_hh_income", label: "Median household income", fmt: (v) => dollars(v as number) },
  { key: "median_home_value", label: "Median home value", fmt: (v) => dollars(v as number) },
  { key: "owner_occupied_pct", label: "Owner-occupied", fmt: (v) => pct1(v as number) },
  { key: "renter_occupied_pct", label: "Renter-occupied", fmt: (v) => pct1(v as number) },
  { key: "median_gross_rent", label: "Median rent", fmt: (v) => dollars(v as number) },
  { key: "total_housing_units", label: "Housing units", fmt: (v) => count(v as number) },
  { key: "bachelors_plus_pct", label: "Bachelor's degree or higher", fmt: (v) => pct1(v as number) },
  { key: "unemployment_pct", label: "Unemployment", fmt: (v) => pct1(v as number) },
  { key: "poverty_pct", label: "Below poverty level", fmt: (v) => pct1(v as number) },
  { key: "white_pct", label: "White", fmt: (v) => pct1(v as number) },
  { key: "hispanic_pct", label: "Hispanic or Latino", fmt: (v) => pct1(v as number) },
  { key: "asian_pct", label: "Asian", fmt: (v) => pct1(v as number) },
  { key: "black_pct", label: "Black", fmt: (v) => pct1(v as number) },
];

const LANGUAGE_ROWS: { key: string; label: string }[] = [
  { key: "english_only_pct", label: "English only" },
  { key: "other_than_english_pct", label: "Another language" },
  { key: "spanish_pct", label: "Spanish" },
  { key: "spanish_limited_english_pct", label: "Spanish, English less than very well" },
  { key: "asian_pacific_lang_pct", label: "Asian and Pacific Island languages" },
  { key: "other_indo_european_pct", label: "Other Indo-European languages" },
];

function ZipPanel(props: { zip: string; row: TerritoryRow | null; onClose: () => void; onChanged: () => Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const [d, setD] = useState<ZipDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allRoutes, setAllRoutes] = useState(false);
  const [status, setStatus] = useState<LifecycleStatus>(props.row?.lifecycle_status ?? "prospect");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setD(await ops.zipDetail(props.zip));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [props.zip]);
  useEffect(() => {
    void load();
  }, [load]);

  const current = (props.row?.lifecycle_status ?? "prospect") as LifecycleStatus;
  const reasonOk = reason.trim().length >= 5;

  async function saveStatus() {
    setBusy(true);
    setError(null);
    try {
      await ops.upsertZip({ zip: props.zip, status }, reason.trim());
      toast(`✓ ${props.zip} is now ${LIFECYCLE_LABEL[status]}`);
      setReason("");
      await Promise.all([load(), props.onChanged()]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const census = d?.census ?? null;
  const lang = d?.language ?? null;
  const routes = d?.routes ?? [];
  const mail = d?.mail_summary ?? null;

  return (
    <Panel kicker="ZIP" title={`${props.zip}${props.row?.label ? ` · ${props.row.label}` : ""}`} onClose={props.onClose}>
      {!d && !error && <Loading what="the ZIP" />}
      {error && <Notice kind="error">{error}</Notice>}
      {d && (
        <>
          <p className="summary-line">
            <LifecycleChip status={current} />
            {props.row ? ` ${props.row.maestros_serving} ${props.row.maestros_serving === 1 ? "Maestro" : "Maestros"} serving · ${props.row.paid_jobs_30d} paid ${props.row.paid_jobs_30d === 1 ? "job" : "jobs"} in 30 days` : ""}
          </p>

          {canApply(role) && (
            <div className="inline-form">
              <div className="field">
                <label htmlFor="zip-status">Change status</label>
                <select id="zip-status" value={status} onChange={(e) => setStatus(e.target.value as LifecycleStatus)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {LIFECYCLE_LABEL[s]}
                    </option>
                  ))}
                </select>
              </div>
              {status !== current && (
                <>
                  <textarea className="reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why? This goes in the change log." aria-label="Reason" />
                  <p className="hint">
                    {status === "paused" ? "Pausing also stops the monthly census refresh for this ZIP." : "The monthly census refresh keeps running for this ZIP."}
                  </p>
                  <button type="button" className="btn primary" disabled={!reasonOk || busy} onClick={saveStatus}>
                    {busy ? "Saving…" : "Save status"}
                  </button>
                </>
              )}
            </div>
          )}

          <SectionLabel>Maestros serving</SectionLabel>
          {d.maestros.length === 0 ? (
            <p className="muted">No Maestro has this ZIP active.</p>
          ) : (
            <ul className="plain-list">
              {d.maestros.map((m) => (
                <li key={m.id}>
                  {m.name ?? "Unnamed"} <span className="muted">· {m.titulo ?? "Sin título"}{m.auto_accept ? " · auto-accept" : ""}</span>
                </li>
              ))}
            </ul>
          )}

          <SectionLabel>Campaigns</SectionLabel>
          {d.campaigns.length === 0 ? (
            <p className="muted">No campaigns here yet.</p>
          ) : (
            <ul className="plain-list">
              {d.campaigns.map((c) => (
                <li key={c.id}>
                  {c.name} <span className="muted">· {c.start_date ?? "—"} to {c.end_date ?? "—"}</span>
                </li>
              ))}
            </ul>
          )}

          <SectionLabel>Language at home (people 5 and over)</SectionLabel>
          {!lang || LANGUAGE_ROWS.every((r) => lang[r.key] === null || lang[r.key] === undefined) ? (
            <p className="muted">No language data yet. It fills in on the next census refresh.</p>
          ) : (
            <dl className="facts two-col">
              {LANGUAGE_ROWS.map((r) => (
                <div key={r.key} className="fact">
                  <dt>{r.label}</dt>
                  <dd>{pct1(lang[r.key] as number | null)}</dd>
                </div>
              ))}
            </dl>
          )}

          <SectionLabel>Census {census?.dataset_year ? `(ACS ${String(census.dataset_year)} 5-year)` : ""}</SectionLabel>
          {!census ? (
            <p className="muted">No census data for this ZIP.</p>
          ) : (
            <dl className="facts two-col">
              {CENSUS_ROWS.map((r) => (
                <div key={r.key} className="fact">
                  <dt>{r.label}</dt>
                  <dd>{r.fmt(census[r.key])}</dd>
                </div>
              ))}
            </dl>
          )}
          <p className="hint">Race and language figures are context for how to talk to a ZIP. They are never used to decide who gets an offer, a price or a Maestro.</p>

          <ZipOnboardingBlock zip={props.zip} />

          <SectionLabel>Mail routes</SectionLabel>
          {mail && (
            <p>
              {count(mail.eddm_eligible_routes as number)} mailable routes · {count(mail.eddm_res_addresses as number)} homes
              {mail.newest_fetch ? <span className="muted"> · updated {dateTime(String(mail.newest_fetch))}</span> : null}
            </p>
          )}
          {routes.length === 0 ? (
            <p className="muted">No route data.</p>
          ) : (
            <div className="table-scroll">
              <table className="data-table compact">
                <thead>
                  <tr>
                    <th scope="col">Route</th>
                    <th scope="col" className="num">Homes</th>
                    <th scope="col" className="num">Businesses</th>
                    <th scope="col" className="num">Median income</th>
                    <th scope="col" className="num">Median age</th>
                  </tr>
                </thead>
                <tbody>
                  {(allRoutes ? routes : routes.slice(0, 20)).map((r) => (
                    <tr key={r.carrier_route}>
                      <td className="mono">{r.carrier_route}</td>
                      <td className="num">{count(r.residences)}</td>
                      <td className="num">{count(r.businesses)}</td>
                      <td className="num">{dollars(r.median_income)}</td>
                      <td className="num">{r.median_age ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!allRoutes && routes.length > 20 && (
            <button type="button" className="btn quiet" onClick={() => setAllRoutes(true)}>
              Show all {routes.length} routes
            </button>
          )}

          <RoutePerformance zip={props.zip} />

          <LotSizesByRoute zip={props.zip} />
        </>
      )}
    </Panel>
  );
}

// ── Data onboarding (zip-onboard, 00025 v8 D3) ─────────────
// Census → mail routes → parcel dry run → an owner or pricing admin confirms → parcels → route linking → Monitor 9.
// The dry run writes nothing; "Confirm parcel import" is the only step that adds houses.

const STEP_LABEL: Record<OnboardingStepKey, string> = {
  census: "Census",
  eddm: "Mail routes (USPS)",
  parcels_dry: "Parcels — dry run",
  parcels: "Parcels — import",
  routes: "Houses linked to routes",
  monitor9: "Data check (Monitor 9)",
};
const STEP_ORDER: OnboardingStepKey[] = ["census", "eddm", "parcels_dry", "parcels", "routes", "monitor9"];
const ONBOARDING_LABEL: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  awaiting_confirm: "Waiting for your confirm",
  confirmed: "Confirmed — importing",
  done: "Done",
  failed: "Stopped",
  no_parcel_source: "No parcel source",
};

function stepDetail(key: OnboardingStepKey, r: Record<string, unknown> | null | undefined): string {
  if (!r) return "";
  const n = (v: unknown) => (typeof v === "number" ? count(v) : "—");
  if (key === "parcels_dry") {
    const types = r.by_property_type as Record<string, number> | undefined;
    return `${n(r.fetched)} homes found${types ? " · " + Object.entries(types).map(([k, v]) => `${k.replace("_", " ")} ${count(v)}`).join(", ") : ""}`;
  }
  if (key === "parcels") return `${n(r.inserted)} added · ${n(r.updated)} matched · ${n(r.address_conflicts)} shared addresses`;
  if (key === "routes") {
    const a = (r.assignment ?? {}) as Record<string, unknown>;
    return `${n(a.assigned)} homes linked · median ${n(a.median_m)} m from route`;
  }
  if (key === "monitor9") return `${String(r.status ?? "")} · ${n(r.properties_loaded)} homes · ${n(r.has_stories)} with stories`;
  if (key === "eddm") return r.this_zip_has_routes === false ? "no routes for this ZIP" : `${n(r.total_routes)} routes (all active ZIPs)`;
  if (key === "census") return typeof r.note === "string" ? r.note : "";
  return "";
}

function ZipOnboardingBlock({ zip }: { zip: string }) {
  const { role } = useOperator();
  const toast = useToast();
  const [o, setO] = useState<ZipOnboarding | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const allowed = canApply(role);

  const load = useCallback(async () => {
    try {
      setO(await ops.zipOnboarding(zip));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [zip]);
  useEffect(() => {
    void load();
  }, [load]);
  // Refresh while the chain is working in the background.
  const active = o?.status === "queued" || o?.status === "running" || o?.status === "confirmed";
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [active, load]);

  async function run(kind: "start" | "confirm") {
    setBusy(true);
    setError(null);
    try {
      if (kind === "start") await ops.startZipOnboarding(zip);
      else await ops.confirmZipOnboarding(zip);
      toast(kind === "start" ? `✓ Onboarding started for ${zip}` : `✓ Parcel import confirmed for ${zip}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (o === undefined && !error) return <Loading what="onboarding" />;
  const steps = o?.steps ?? {};
  const dry = steps.parcels_dry?.status === "ok";
  const canConfirm = !!o && dry && (o.status === "awaiting_confirm" || o.status === "failed");

  return (
    <>
      <SectionLabel>Data onboarding</SectionLabel>
      {error && <Notice kind="error">{error}</Notice>}
      {o === null ? (
        <>
          <p className="muted">This ZIP has never been onboarded: no parcels or route links yet.</p>
          {allowed && (
            <button type="button" className="btn" disabled={busy} onClick={() => void run("start")}>
              {busy ? "Starting…" : "Start onboarding →"}
            </button>
          )}
        </>
      ) : o ? (
        <>
          <p className="summary-line">
            <Chip tone={o.status === "done" ? "info" : o.status === "failed" || o.status === "no_parcel_source" ? "warn" : undefined}>
              {ONBOARDING_LABEL[o.status] ?? o.status}
            </Chip>
            <span className="muted">
              {" "}· {o.county ?? "county unknown"} · updated {dateTime(o.updated_at)}
              {o.confirmed_by ? ` · confirmed by ${o.confirmed_by}` : ""}
            </span>
          </p>
          {o.error && <Notice kind="error">{o.error}</Notice>}
          {typeof steps.note === "string" ? (
            <p className="muted">{steps.note}</p>
          ) : (
            <ul className="plain-list">
              {STEP_ORDER.filter((k) => steps[k]).map((k) => {
                const s = steps[k]!;
                const mark = s.status === "ok" ? "✓" : s.status === "failed" ? "✕" : s.status === "running" ? "…" : "–";
                return (
                  <li key={k}>
                    {mark} {STEP_LABEL[k]} <span className="muted">· {stepDetail(k, s.result as Record<string, unknown> | null)}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {canConfirm && allowed && (
            <>
              <p className="hint">The dry run wrote nothing. Confirming adds these homes to properties, links them to mail routes and runs the data check.</p>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void run("confirm")}>
                {busy ? "Confirming…" : o.status === "failed" ? "Retry the import →" : "Confirm parcel import →"}
              </button>
            </>
          )}
          {o.status === "failed" && !dry && allowed && (
            <button type="button" className="btn" disabled={busy} onClick={() => void run("start")}>
              {busy ? "Starting…" : "Retry →"}
            </button>
          )}
        </>
      ) : null}
    </>
  );
}

// ── Lot sizes by route (00025 v8 D3) ───────────────────────
// Which mail routes are mostly lots we can price on the spot. Homes come from the county assessor
// (houses only); "USPS homes" is what a mailer to the route reaches (includes apartments).

const LOT_CHOICES = [5000, 7500, 10000, 15000, 20000];
const SHARE_CHOICES = [70, 80, 90];

function LotSizesByRoute({ zip }: { zip: string }) {
  const [maxLot, setMaxLot] = useState(10000);
  const [share, setShare] = useState(80);
  const [m, setM] = useState<RouteSizeMix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  useEffect(() => {
    let live = true;
    setM(null);
    setError(null);
    ops
      .routeSizeMix(zip, maxLot, share)
      .then((r) => { if (live) setM(r); })
      .catch((e) => { if (live) setError((e as Error).message); });
    return () => { live = false; };
  }, [zip, maxLot, share]);

  const rows = m?.routes ?? [];
  const s = m?.summary;
  const lotLabel = `${count(maxLot)} sq ft`;

  return (
    <>
      <SectionLabel>Lot sizes by route</SectionLabel>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Lot size limit">
          {LOT_CHOICES.map((v) => (
            <button key={v} type="button" className={maxLot === v ? "seg on" : "seg"} aria-pressed={maxLot === v} onClick={() => setMaxLot(v)}>
              {"< "}{count(v)}
            </button>
          ))}
        </div>
        <div className="segmented" role="group" aria-label="Share of homes needed">
          {SHARE_CHOICES.map((v) => (
            <button key={v} type="button" className={share === v ? "seg on" : "seg"} aria-pressed={share === v} onClick={() => setShare(v)}>
              {v}%+
            </button>
          ))}
        </div>
      </div>

      {error && <Notice kind="error">{error}</Notice>}
      {!m && !error && <Loading what="lot sizes" />}
      {m && s && (s.routes === 0 ? (
        <p className="muted">No homes are linked to routes in this ZIP yet. Run the parcel import and route linking for {zip} first.</p>
      ) : (
        <>
          <p className="summary-line">
            {s.priceable_routes} of {s.routes} routes have {share}% or more of homes on lots under {lotLabel}
            {s.priceable_routes > 0 ? ` · ${count(s.homes_on_priceable_routes)} homes on them · ${count(s.usps_res_on_priceable_routes)} USPS homes to mail` : ""}
            <span className="muted"> · {count(s.homes_under_max)} of {count(s.homes)} homes in the ZIP are under {lotLabel}</span>
          </p>
          <div className="table-scroll">
            <table className="data-table compact">
              <thead>
                <tr>
                  <th scope="col">Route</th>
                  <th scope="col" className="num">Homes</th>
                  <th scope="col" className="num">Under {count(maxLot)}</th>
                  <th scope="col" className="num">Share</th>
                  <th scope="col" className="num">1 acre+ (quote)</th>
                  <th scope="col" className="num">Median lot</th>
                  <th scope="col" className="num">USPS homes</th>
                </tr>
              </thead>
              <tbody>
                {(all ? rows : rows.slice(0, 20)).map((r) => (
                  <tr key={r.carrier_route}>
                    <td className="mono">
                      {r.carrier_route} {r.priceable && <Chip tone="info">Priceable</Chip>}
                    </td>
                    <td className="num">{count(r.homes)}</td>
                    <td className="num">{count(r.under_max)}</td>
                    <td className="num">{pct1(r.pct_under_max)}</td>
                    <td className="num">{pct1(r.pct_custom)}</td>
                    <td className="num">{r.median_lot === null ? "—" : `${count(r.median_lot)} sq ft`}</td>
                    <td className="num">{count(r.usps_res_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!all && rows.length > 20 && (
            <button type="button" className="btn quiet" onClick={() => setAll(true)}>
              Show all {rows.length} routes
            </button>
          )}
          <p className="hint">
            Homes are houses from the county assessor; apartments and condos aren't counted, so USPS homes (what a mailer reaches) is higher.
            Lots of 1 acre or more are quote-only. House size and stories join this table once the assessor's improvement records are loaded.
          </p>
        </>
      ))}
    </>
  );
}

// ── Route performance (OPS-METRICS-01) ─────────────────────
// Is the flywheel working on each mail route? Five tests, scored nightly (cron flywheel-route-snapshot).
// A route is lit when all five pass for the required months in a row; cooling when it was lit and isn't now.

const STATE_LABEL: Record<RouteState, string> = { lit: "Lit", warming: "Warming", cooling: "Cooling", cold: "Cold" };
const TEST_LABEL: Record<LitTest, string> = {
  volume: "Jobs this month",
  pays_its_mail: "Pays its own mail",
  organic: "Jobs not from this month's flyer",
  liquidity: "Fast yeses, enough Maestros",
  season_loop: "Customers come back",
};
const TEST_ORDER: LitTest[] = ["volume", "pays_its_mail", "organic", "liquidity", "season_loop"];

function RouteStateChip({ state }: { state: RouteState }) {
  const tone = state === "lit" ? "info" : state === "cooling" ? "warn" : state === "cold" ? "muted" : undefined;
  return <Chip tone={tone}>{STATE_LABEL[state]}</Chip>;
}

const share = (f: number | null) => (f === null ? "—" : pct1(f * 100));
const minutes = (m: number | null) => (m === null ? "—" : m < 90 ? `${Math.round(m)} min` : `${(m / 60).toFixed(1)} h`);

function RoutePerformance({ zip }: { zip: string }) {
  const { role } = useOperator();
  const [b, setB] = useState<RouteBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);

  const load = useCallback(async () => {
    try {
      setB(await ops.routeLitBoard(zip));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [zip]);
  useEffect(() => {
    setB(null);
    void load();
  }, [load]);

  const rows = b?.routes ?? [];
  const t = b?.totals;
  const z = b?.zips?.[0];

  return (
    <>
      <SectionLabel>How mailed routes are doing</SectionLabel>
      {error && <Notice kind="error">{error}</Notice>}
      {!b && !error && <Loading what="route performance" />}
      {b && t && (rows.length === 0 ? (
        <p className="muted">No routes are scored for {zip} yet. Routes are scored nightly for ZIPs that are in service.</p>
      ) : (
        <>
          <p className="summary-line">
            {t.lit} of {t.routes} routes lit · {t.warming} warming · {t.cooling} cooling
            {z ? ` · ${count(z.jobs)} ${z.jobs === 1 ? "job" : "jobs"} this month · ${money(z.surplus_cents)} after mail` : ""}
            <span className="muted">{b.computed_at ? ` · scored ${dateTime(b.computed_at)}` : ""}</span>
          </p>
          {z && z.unassigned_jobs > 0 && (
            <Notice kind="info">
              {z.unassigned_jobs} {z.unassigned_jobs === 1 ? "job" : "jobs"} in {zip} this month {z.unassigned_jobs === 1 ? "isn't" : "aren't"} linked to a house, so no route gets credit for {z.unassigned_jobs === 1 ? "it" : "them"}.
            </Notice>
          )}
          <div className="table-scroll">
            <table className="data-table compact">
              <thead>
                <tr>
                  <th scope="col">Route</th>
                  <th scope="col">State</th>
                  <th scope="col" className="num">Jobs</th>
                  <th scope="col" className="num">Not from flyer</th>
                  <th scope="col" className="num">Mail paid back</th>
                  <th scope="col" className="num">Time to yes</th>
                  <th scope="col" className="num">Maestros</th>
                  <th scope="col">Next gap</th>
                </tr>
              </thead>
              <tbody>
                {(all ? rows : rows.slice(0, 20)).map((r) => (
                  <RouteRow key={r.route} r={r} open={open === r.route} onToggle={() => setOpen(open === r.route ? null : r.route)} />
                ))}
              </tbody>
            </table>
          </div>
          {!all && rows.length > 20 && (
            <button type="button" className="btn quiet" onClick={() => setAll(true)}>
              Show all {rows.length} routes
            </button>
          )}
          <p className="hint">
            A route is lit when all five tests pass for {b.thresholds.lit_months_required} months in a row. Jobs count on a route through the house they're for.
            Mail paid back = what the route's jobs earned after Maestro pay and costs, divided by what its flyers cost.{" "}
            <button type="button" className="btn quiet" onClick={() => setShowRules(!showRules)} aria-expanded={showRules}>
              {showRules ? "Hide the rules" : "See the rules"}
            </button>
          </p>
          {showRules && <RouteRules s={b.thresholds} canEdit={role === "owner"} onSaved={load} />}
        </>
      ))}
    </>
  );
}

function RouteRow({ r, open, onToggle }: { r: RouteBoardRow; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr>
        <td className="mono">
          <button type="button" className="btn quiet" onClick={onToggle} aria-expanded={open} aria-label={`Route ${r.route} details`}>
            {r.route}
          </button>
        </td>
        <td><RouteStateChip state={r.state} /></td>
        <td className="num">{count(r.jobs)}</td>
        <td className="num">{share(r.organic_share)}</td>
        <td className="num">{r.mail_multiple === null ? (r.marketing_cents === 0 ? "No mail" : "—") : `${r.mail_multiple.toFixed(1)}×`}</td>
        <td className="num">{minutes(r.median_accept_min)}</td>
        <td className="num">{count(r.active_maestros)}</td>
        <td>{r.missing[0] ?? <span className="muted">All tests pass</span>}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8}>
            <dl className="facts two-col">
              {TEST_ORDER.map((k) => (
                <div key={k} className="fact">
                  <dt>{TEST_LABEL[k]}</dt>
                  <dd>
                    {r.tests[k] === "pass" ? <Chip tone="info">Pass</Chip> : r.tests[k] === "fail" ? <Chip tone="warn">Not yet</Chip> : <Chip tone="muted">Not enough data</Chip>}
                  </dd>
                </div>
              ))}
              <div className="fact">
                <dt>Where jobs came from</dt>
                <dd>{r.sources.flyer} flyer · {r.sources.repeat} repeat · {r.sources.starred} favorite Maestro · {r.sources.direct} other</dd>
              </div>
              <div className="fact">
                <dt>Earned after costs / flyer cost</dt>
                <dd>{money(r.contribution_cents)} / {r.marketing_cents > 0 ? money(r.marketing_cents) : "no mail this month"}</dd>
              </div>
              <div className="fact">
                <dt>Filled by the second text</dt>
                <dd>{share(r.fill_by_step2)}</dd>
              </div>
              <div className="fact">
                <dt>Book a second service in 6 months</dt>
                <dd>{share(r.second_service_share)}</dd>
              </div>
            </dl>
            {r.missing.length > 1 && (
              <ul className="plain-list">
                {r.missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

type RuleField = { key: keyof FlywheelSettings; label: string; kind: "int" | "num" | "pct" | "money" };
const RULE_FIELDS: RuleField[] = [
  { key: "lit_min_jobs_month", label: "Jobs a month (at least)", kind: "int" },
  { key: "lit_min_mail_multiple", label: "Mail paid back (at least ×)", kind: "num" },
  { key: "lit_min_organic_share", label: "Jobs not from this month's flyer (at least %)", kind: "pct" },
  { key: "lit_max_median_accept_min", label: "Time to yes (at most, minutes)", kind: "int" },
  { key: "lit_min_fill_by_step2", label: "Filled by the second text (at least %)", kind: "pct" },
  { key: "lit_min_active_maestros", label: "Active Maestros (at least)", kind: "int" },
  { key: "lit_min_second_service", label: "Book a second service in 6 months (at least %)", kind: "pct" },
  { key: "lit_months_required", label: "Months in a row to be lit", kind: "int" },
  { key: "maestro_radius_km", label: "A Maestro covers a route within (km)", kind: "num" },
  { key: "breakeven_fixed_cents_month", label: "Fixed costs a month (breakeven)", kind: "money" },
  { key: "next_zip_launch_cents", label: "Budget to open the next ZIP", kind: "money" },
];
const ruleText = (f: RuleField, v: number) => (f.kind === "pct" ? String(Math.round(v * 1000) / 10) : f.kind === "money" ? toDollarText(v) : String(v));
const ruleShow = (f: RuleField, v: number) => (f.kind === "pct" ? pct1(v * 100) : f.kind === "money" ? money(v) : String(v));

function RouteRules(props: { s: FlywheelSettings; canEdit: boolean; onSaved: () => Promise<void> }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries(RULE_FIELDS.map((f) => [f.key, ruleText(f, props.s[f.key])])));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function patch(): Partial<FlywheelSettings> | string {
    const out: Partial<FlywheelSettings> = {};
    for (const f of RULE_FIELDS) {
      const raw = (vals[f.key] ?? "").trim();
      let v: number | null;
      if (f.kind === "money") v = parseDollars(raw);
      else if (f.kind === "pct") v = raw === "" || !Number.isFinite(Number(raw)) ? null : Number(raw) / 100;
      else v = raw === "" || !Number.isFinite(Number(raw)) ? null : Number(raw);
      if (v === null || v < 0 || (f.kind === "int" && !Number.isInteger(v)) || (f.kind === "pct" && v > 1)) return `${f.label}: enter a valid number.`;
      if (v !== Number(props.s[f.key])) (out as Record<string, number>)[f.key] = v;
    }
    return out;
  }

  async function save() {
    const p = patch();
    if (typeof p === "string") { setError(p); return; }
    if (Object.keys(p).length === 0) { setError("Nothing changed."); return; }
    setBusy(true);
    setError(null);
    try {
      await ops.setFlywheelSettings(p, reason.trim());
      toast("✓ Route rules saved — this month is re-scored");
      setEditing(false);
      setReason("");
      await props.onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <>
        <dl className="facts two-col">
          {RULE_FIELDS.map((f) => (
            <div key={f.key} className="fact">
              <dt>{f.label}</dt>
              <dd>{ruleShow(f, props.s[f.key])}</dd>
            </div>
          ))}
        </dl>
        {props.canEdit && (
          <button type="button" className="btn" onClick={() => setEditing(true)}>
            Change the rules…
          </button>
        )}
      </>
    );
  }
  return (
    <div className="inline-form">
      <p className="action-title">Change the route rules</p>
      <div className="facts two-col">
        {RULE_FIELDS.map((f) => (
          <Field key={f.key} label={f.label} htmlFor={`rule-${f.key}`}>
            <input id={`rule-${f.key}`} inputMode="decimal" value={vals[f.key]} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} />
          </Field>
        ))}
      </div>
      <textarea className="reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why? This goes in the change log." aria-label="Reason" />
      <p className="hint">This month's scores update as soon as you save. Earlier months keep the rules they were scored with.</p>
      {error && <Notice kind="error">{error}</Notice>}
      <div className="row-actions">
        <button type="button" className="btn primary" disabled={busy || reason.trim().length < 5} onClick={save}>
          {busy ? "Saving…" : "Save rules"}
        </button>
        <button type="button" className="btn quiet" onClick={() => { setEditing(false); setError(null); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Add ZIP / Import ───────────────────────────────────────

function AddZipPanel(props: { onClose: () => void; onDone: (zip: string) => Promise<void> }) {
  const toast = useToast();
  const [zip, setZip] = useState("");
  const [label, setLabel] = useState("");
  const [status, setStatus] = useState<LifecycleStatus>("prospect");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = /^\d{5}$/.test(zip.trim()) && reason.trim().length >= 5;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const z = zip.trim();
      await ops.upsertZip({ zip: z, ...(label.trim() ? { label: label.trim() } : {}), status }, reason.trim());
      toast(`✓ ${z} added as ${LIFECYCLE_LABEL[status]}`);
      await props.onDone(z);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel kicker="Territories" title="Add a ZIP" onClose={props.onClose}>
      <div className="field">
        <label htmlFor="add-zip">ZIP code</label>
        <input id="add-zip" inputMode="numeric" maxLength={5} value={zip} onChange={(e) => setZip(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="add-label">Area name</label>
        <input id="add-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Mill Creek" />
      </div>
      <div className="field">
        <label htmlFor="add-status">Status</label>
        <select id="add-status" value={status} onChange={(e) => setStatus(e.target.value as LifecycleStatus)}>
          {STATUSES.filter((s) => s !== "paused").map((s) => (
            <option key={s} value={s}>
              {LIFECYCLE_LABEL[s]}
            </option>
          ))}
        </select>
      </div>
      <textarea className="reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this ZIP? This goes in the change log." aria-label="Reason" />
      <p className="hint">Census figures and mail routes for a new ZIP arrive with the next census refresh.</p>
      {error && <Notice kind="error">{error}</Notice>}
      <button type="button" className="btn primary wide" disabled={!ok || busy} onClick={save}>
        {busy ? "Adding…" : "Add ZIP"}
      </button>
    </Panel>
  );
}

function ImportPanel(props: { onClose: () => void; onDone: () => Promise<void> }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsed = text
    .split("\n")
    .map((l) => l.split(",").map((s) => s.trim()))
    .filter((p) => /^\d{5}$/.test(p[0] ?? ""))
    .map((p) => ({ zip_code: p[0], label: p[1] || p[0] }));

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const r = await ops.importZips(parsed);
      toast(`✓ Imported ${r.imported}${r.skipped ? `, ${r.skipped} already listed` : ""}`);
      await props.onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel kicker="Territories" title="Import ZIPs" onClose={props.onClose}>
      <p>One ZIP per line, with an optional area name after a comma. Imported ZIPs get their census data right away.</p>
      <textarea className="reason" rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={"98012, Mill Creek\n98087, Lynnwood"} aria-label="ZIPs to import" />
      <p className="hint">{parsed.length} valid {parsed.length === 1 ? "ZIP" : "ZIPs"}.</p>
      {error && <Notice kind="error">{error}</Notice>}
      <button type="button" className="btn primary wide" disabled={parsed.length === 0 || busy} onClick={run}>
        {busy ? "Importing…" : `Import ${parsed.length} ${parsed.length === 1 ? "ZIP" : "ZIPs"}`}
      </button>
    </Panel>
  );
}
